import { html } from '../html.js';
import type { VNode } from '../html.js';
import { CopyButton } from './copy.js';

/**
 * The two surfaces, as two URLs — and the snippets every platform reads them in.
 *
 * They are shown side by side because the product's claim is that one manifest
 * serves both: a branded page an agent drives through `document.modelContext`,
 * and a remote MCP server whose predeclared `ui://` resources render the same
 * views inside a chat client. Anything that makes those look like two products
 * is a design bug.
 *
 * The snippet builders below are pure string functions, kept here rather than
 * in the Connect screen so the onboarding console's result page and the
 * dashboard's Connect screen hand out byte-identical configuration. Each
 * builder encodes one platform's config shape, verified against the client's
 * own documentation on 2026-09-08; when a client changes its shape, this is the
 * one file to edit.
 *
 * Every snippet says the same thing in a different dialect: a Streamable HTTP
 * server at `<url>`, no auth, no headers, no command. None of them can add a
 * credential, because there is no credential to add — the endpoint is keyless
 * and read-only by construction.
 */

export interface EndpointProps {
  webUrl: string | null;
  mcpUrl: string | null;
  tenantId: string;
  copied: string | null;
  onCopy: (id: string, text: string) => void;
}

/** The address a tenant answers MCP on, from the origin a page was served from. */
export function tenantUrls(origin: string, tenantId: string): {
  page: string;
  mcp: string;
  embedJs: string;
  icon: string;
  /**
   * RFC 9728's path-inserted well-known for this endpoint. The endpoint is
   * keyless, so nothing is served there today; it is named because it is the
   * path a proxy must keep reachable if the tenant later moves under its own
   * domain, and the path an OAuth-aware client would probe first.
   */
  protectedResourceMetadata: string;
} {
  const page = `${origin}/t/${tenantId}`;
  return {
    page,
    mcp: `${page}/mcp`,
    embedJs: `${page}/embed.js`,
    icon: `${page}/icon.svg`,
    protectedResourceMetadata: `${origin}/.well-known/oauth-protected-resource/t/${tenantId}/mcp`,
  };
}

/**
 * Claude Code's `.mcp.json` and Claude Desktop's `claude_desktop_config.json`
 * share the `mcpServers` map; a remote server is `type: "http"` plus the URL.
 */
export function mcpClientConfig(tenantId: string, mcpUrl: string): string {
  return JSON.stringify(
    { mcpServers: { [tenantId]: { type: 'http', url: mcpUrl } } },
    null,
    2,
  );
}

/** `claude mcp add`: the one-line form of the JSON above. */
export function claudeCodeCommand(tenantId: string, mcpUrl: string): string {
  return `claude mcp add --transport http ${tenantId} ${mcpUrl}`;
}

/**
 * Cursor (`.cursor/mcp.json`) and Windsurf (`mcp_config.json`) infer the
 * transport from the presence of `url`; a `type` key is not part of their
 * shape, so it is left out rather than tolerated.
 */
export function mcpJsonConfig(tenantId: string, mcpUrl: string): string {
  return JSON.stringify({ mcpServers: { [tenantId]: { url: mcpUrl } } }, null, 2);
}

/** VS Code's `.vscode/mcp.json` uses `servers`, not `mcpServers`, and names the transport. */
export function vsCodeConfig(tenantId: string, mcpUrl: string): string {
  return JSON.stringify({ servers: { [tenantId]: { type: 'http', url: mcpUrl } } }, null, 2);
}

/** Gemini CLI's `settings.json`: `httpUrl` selects Streamable HTTP (`url` alone would mean SSE). */
export function geminiCliConfig(tenantId: string, mcpUrl: string): string {
  return JSON.stringify({ mcpServers: { [tenantId]: { httpUrl: mcpUrl } } }, null, 2);
}

/** The WebMCP embed tag: the tenant id travels in the script's own URL and nowhere else. */
export function embedTag(embedJsUrl: string): string {
  return `<script src="${embedJsUrl}" async></script>`;
}

/**
 * The handshake a client makes first, as a curl a person can paste. The
 * `accept` header names both media types because a Streamable HTTP server may
 * answer either, and a client that omits it is refused by some servers.
 */
export function curlInitialize(mcpUrl: string, protocolVersion: string): string {
  return [
    `curl -s ${mcpUrl} \\`,
    `  -H 'content-type: application/json' \\`,
    `  -H 'accept: application/json, text/event-stream' \\`,
    `  -d '{"jsonrpc":"2.0","id":1,"method":"initialize","params":{"protocolVersion":"${protocolVersion}","capabilities":{},"clientInfo":{"name":"curl","version":"0"}}}'`,
  ].join('\n');
}

/**
 * What a proxy on the tenant's own domain forwards, and where to.
 *
 * Three paths and no more. `/mcp` is the endpoint; the RFC 9728 well-known is
 * kept reachable so an OAuth-aware client probing it gets the server's answer
 * rather than the tenant's 404 page; the icon is optional. Widgets and the data
 * plane stay on the ClawHarbor origin on purpose: the widget's CSP names that
 * origin, so rewriting them onto the tenant's domain produces a widget whose
 * own CSP forbids its own data plane.
 *
 * Each builder below is docs/INTEGRATE.md §2 with the placeholders filled in,
 * and with two mistakes the static doc carried corrected: the Caddy block put
 * `rewrite` inside `reverse_proxy`, which Caddy refuses to parse, and the
 * Worker route `/mcp*` forwarded `/mcp/icon.svg` to a path that does not exist.
 */
export interface ProxyTarget {
  /** The tenant's own hostname, bare: `hep.hr`. */
  host: string;
  /** `https://mcp.clawharbor.io/t/<id>/mcp`, exactly as the server named it. */
  mcp: string;
  protectedResourceMetadata: string;
  icon: string;
}

function pathOf(url: string): string {
  const u = new URL(url);
  return u.pathname + u.search;
}

export function proxyPaths(t: ProxyTarget): Array<{ from: string; to: string; note: string }> {
  return [
    { from: '/mcp', to: t.mcp, note: 'GET, POST, DELETE, OPTIONS. Streamable HTTP: never buffer text/event-stream.' },
    {
      from: '/.well-known/oauth-protected-resource/mcp',
      to: t.protectedResourceMetadata,
      note: 'Answers 404 today: no auth server is advertised yet. Wiring it now costs nothing.',
    },
    { from: '/mcp/icon.svg', to: t.icon, note: 'Optional. The icon agents show beside the name.' },
  ];
}

export function proxyNginx(t: ProxyTarget): string {
  const up = new URL(t.mcp).host;
  const block = (from: string, to: string, stream: boolean): string =>
    [
      `location = ${from} {`,
      `  proxy_pass ${to};`,
      `  proxy_set_header Host ${up};`,
      `  proxy_set_header X-Forwarded-Host ${t.host};`,
      `  proxy_set_header X-Forwarded-Proto https;`,
      `  proxy_ssl_server_name on;`,
      ...(stream
        ? [`  proxy_http_version 1.1;`, `  proxy_buffering off;   # the SSE stream must not be held back`, `  proxy_read_timeout 300s;`]
        : []),
      `}`,
    ].join('\n');
  return [
    block('/mcp', t.mcp, true),
    block('/.well-known/oauth-protected-resource/mcp', t.protectedResourceMetadata, false),
    block('/mcp/icon.svg', t.icon, false),
  ].join('\n');
}

export function proxyCaddy(t: ProxyTarget): string {
  const up = new URL(t.mcp).origin;
  const handle = (from: string, to: string, stream: boolean): string =>
    [
      `\thandle ${from} {`,
      `\t\trewrite * ${pathOf(to)}`,
      `\t\treverse_proxy ${up} {`,
      `\t\t\theader_up Host {upstream_hostport}`,
      `\t\t\theader_up X-Forwarded-Host {host}`,
      ...(stream ? [`\t\t\tflush_interval -1`] : []),
      `\t\t}`,
      `\t}`,
    ].join('\n');
  return [
    `${t.host} {`,
    handle('/mcp', t.mcp, true),
    handle('/.well-known/oauth-protected-resource/mcp', t.protectedResourceMetadata, false),
    handle('/mcp/icon.svg', t.icon, false),
    `\t# the rest of your site`,
    `}`,
  ].join('\n');
}

export function proxyCloudflareWorker(t: ProxyTarget): string {
  const map = proxyPaths(t).map((p) => `  '${p.from}': '${p.to}',`);
  return [
    `// Routes: ${t.host}/mcp*  and  ${t.host}/.well-known/oauth-protected-resource/mcp`,
    `const UPSTREAM = {`,
    ...map,
    `};`,
    ``,
    `export default {`,
    `  async fetch(request) {`,
    `    const url = new URL(request.url);`,
    `    const target = UPSTREAM[url.pathname];`,
    `    if (!target) return fetch(request);`,
    `    const headers = new Headers(request.headers);`,
    `    headers.set('x-forwarded-host', url.host);`,
    `    headers.set('x-forwarded-proto', 'https');`,
    `    return fetch(target + url.search, {`,
    `      method: request.method,`,
    `      headers,`,
    `      body: request.body,`,
    `      redirect: 'manual',`,
    `    });`,
    `  },`,
    `};`,
  ].join('\n');
}

export function proxyVercel(t: ProxyTarget): string {
  return JSON.stringify(
    { rewrites: proxyPaths(t).map((p) => ({ source: p.from, destination: p.to })) },
    null,
    2,
  );
}

const FENCE = '```';

function fence(lang: string, body: string): string {
  return `${FENCE}${lang}\n${body}\n${FENCE}`;
}

/**
 * The whole integration guide for one site, as Markdown a person can hand to
 * whoever runs the website. Built from the same builders the Connect tab shows,
 * so the copy and the screen cannot disagree.
 */
export function integrationGuide(g: {
  displayName: string;
  tenantId: string;
  canonicalOrigin: string;
  mcp: string;
  embedJs: string;
  proxy: ProxyTarget | null;
  protocolVersion: string;
}): string {
  const out: string[] = [
    `# ${g.displayName} on ClawHarbor: integration guide`,
    '',
    `Tenant \`${g.tenantId}\`, crawled from ${g.canonicalOrigin}.`,
    '',
    '## 1. Connect an AI client',
    '',
    `The MCP endpoint is ${g.mcp}. Streamable HTTP, no key, read-only tools.`,
    '',
    '- Claude (claude.ai, Desktop): Settings, Connectors, Add custom connector, paste the URL.',
    '- ChatGPT: Settings, Apps & Connectors, Create, paste the URL.',
    '- Claude Code:',
    '',
    fence('bash', claudeCodeCommand(g.tenantId, g.mcp)),
    '',
    '- Cursor, Windsurf and other clients that read an mcpServers map:',
    '',
    fence('json', mcpJsonConfig(g.tenantId, g.mcp)),
    '',
    '## 2. WebMCP on your own pages',
    '',
    'One tag registers the same tools for browser agents that support WebMCP. It writes nothing to the page.',
    '',
    fence('html', embedTag(g.embedJs)),
    '',
  ];
  if (g.proxy) {
    out.push(
      `## 3. Serve the endpoint as https://${g.proxy.host}/mcp`,
      '',
      'Two things must both hold before a browser on your domain is answered: the host is declared in the ClawHarbor console (Connect tab), and a verified ownership claim covers the domain (Overview tab). Non-browser clients work through the proxy either way.',
      '',
      '| your path | forwards to |',
      '|---|---|',
      ...proxyPaths(g.proxy).map((p) => `| \`${p.from}\` | ${p.to} |`),
      '',
      'Forward Content-Type, Accept, Mcp-Session-Id, Mcp-Protocol-Version, Mcp-Method, Mcp-Name and Origin unchanged, both ways. Do not answer OPTIONS yourself, do not rewrite the body, do not buffer or cache.',
      '',
      '### nginx',
      '',
      fence('nginx', proxyNginx(g.proxy)),
      '',
      '### Caddy',
      '',
      fence('caddyfile', proxyCaddy(g.proxy)),
      '',
      '### Cloudflare Worker',
      '',
      fence('js', proxyCloudflareWorker(g.proxy)),
      '',
      '### Vercel (vercel.json)',
      '',
      fence('json', proxyVercel(g.proxy)),
      '',
      'Whether a Vercel external rewrite streams text/event-stream unbuffered is unverified; only legacy clients use that stream.',
      '',
      '### Check it',
      '',
      fence('bash', curlInitialize(`https://${g.proxy.host}/mcp`, g.protocolVersion)),
      '',
    );
  }
  return out.join('\n');
}

function Url(props: {
  tag: string;
  value: string | null;
  pendingText: string;
  copyId: string;
  describe: string;
  copied: string | null;
  onCopy: (id: string, text: string) => void;
}): VNode {
  return html`
    <div class="url">
      <span class="url__tag">${props.tag}</span>
      ${props.value
        ? html`<a class="url__value" href=${props.value} target="_blank" rel="noopener noreferrer">
            ${props.value}
          </a>`
        : html`<span class="url__value url__value--pending">${props.pendingText}</span>`}
      ${props.value
        ? CopyButton({
            id: props.copyId,
            text: props.value,
            copied: props.copied,
            onCopy: props.onCopy,
            describe: props.describe,
          })
        : html`<button type="button" class="copy" disabled>Copy</button>`}
    </div>
  `;
}

export function Endpoints(props: EndpointProps): VNode {
  const config = props.mcpUrl ? mcpClientConfig(props.tenantId, props.mcpUrl) : null;
  const cli = props.mcpUrl ? claudeCodeCommand(props.tenantId, props.mcpUrl) : null;

  return html`
    <div>
    <div class="urls">
      ${Url({
        tag: 'Web',
        value: props.webUrl,
        pendingText: 'Issued when you publish',
        copyId: 'web-url',
        describe: 'the branded page URL',
        copied: props.copied,
        onCopy: props.onCopy,
      })}
      ${Url({
        tag: 'MCP',
        value: props.mcpUrl,
        pendingText: 'Issued when you publish',
        copyId: 'mcp-url',
        describe: 'the MCP endpoint URL',
        copied: props.copied,
        onCopy: props.onCopy,
      })}
    </div>

    ${config && cli
      ? html`
          <div class="snippet" style="margin-top:20px">
            <div class="snippet__bar">
              <span class="snippet__name">claude_desktop_config.json</span>
              ${CopyButton({
                id: 'mcp-config',
                text: config,
                copied: props.copied,
                onCopy: props.onCopy,
                describe: 'the MCP client config',
              })}
            </div>
            <pre class="snippet__code"><code>${config}</code></pre>
            <div class="snippet__bar" style="border-top:1px solid var(--rule);border-bottom:0">
              <span class="snippet__name">${cli}</span>
              ${CopyButton({
                id: 'mcp-cli',
                text: cli,
                copied: props.copied,
                onCopy: props.onCopy,
                label: 'Copy CLI',
                describe: 'the CLI command',
              })}
            </div>
          </div>
        `
      : null}
    </div>
  `;
}
